import { Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { AgentEventLog } from '../../../api/events-client/events-client.type';
import { inkLight, secondaryBase } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { TaskMetadataList } from '../../work-periods/task-detail/task-metadata-list';
import { agentSourcesIcon, formatAgenticProcessDate } from '../agentic-process-helpers';
import { EVENT_TYPES_LABELS } from '../agentic-process.type';

type Props = {
  task?: TaskRecord | null;
  agentEvent: AgentEventLog;
  onClose: () => void;
};

export const AgentEventSidePanel = ({ task, agentEvent, onClose }: Props) => {
  const { t } = useTranslation();
  const eventData = [
    { label: 'Agent Source', value: agentSourcesIcon(agentEvent.agent_source, 0, 16) },
    { label: 'Event Date', value: formatAgenticProcessDate(agentEvent.event_date) || '' },
    { label: 'LLM', value: agentEvent.llm_model || '' },
  ];

  if (task) {
    eventData.push({
      label: t('common.work_unit'),
      value: (
        <a href={task.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <Text size="small" color={secondaryBase}>
            {task.title}
          </Text>
        </a>
      ),
    });
  }

  if (agentEvent.user?.name) {
    eventData.push({ label: t('common.user'), value: agentEvent.user.name });
  }

  return (
    <>
      <Group justify="space-between">
        <Text weight="bold" size="medium">
          {EVENT_TYPES_LABELS[agentEvent.event_type]}
        </Text>
        <Icon name="close" onClick={onClose} />
      </Group>
      <Divider style={{ margin: '16px 0' }} />
      <TaskMetadataList metadata={eventData} textColor={inkLight} />
      <Divider style={{ margin: '16px 0' }} />
      <Text weight="medium" size="medium" style={{ marginBottom: 12 }}>
        Event Metadata
      </Text>

      {agentEvent.metadata && Object.keys(agentEvent.metadata).length > 0 ? (
        <div>
          {Object.entries(agentEvent.metadata)
            .filter(([_, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => (
              <div key={key} style={{ marginBottom: 8 }}>
                <Text size="small" weight="bold" style={{ textTransform: 'capitalize' }}>
                  {key.replace(/_/g, ' ')}
                </Text>
                {typeof value === 'object' && value !== null ? (
                  <div style={{ marginLeft: 16 }}>
                    {Object.entries(value).map(([subKey, subValue]) => (
                      <div key={subKey} style={{ marginBottom: 4 }}>
                        <Text size="small" weight="medium">
                          {subKey.replace(/_/g, ' ')}:
                        </Text>
                        <div>
                          <Text size="small" style={{ marginLeft: 16 }}>
                            {typeof subValue === 'object' && subValue !== null
                              ? JSON.stringify(subValue, null, 2)
                              : String(subValue)}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text size="small">{String(value)}</Text>
                )}
              </div>
            ))}
        </div>
      ) : (
        <Text size="small" color={inkLight}>
          No metadata available for this event.
        </Text>
      )}
    </>
  );
};
